<template>
	<nav class="header-nav">
		<template v-for="link in visibleLinks" :key="link.label">
			<router-link
				v-if="!link.isStub && link.route"
				v-slot="{ href, navigate }"
				:to="linkTarget(link)"
				custom
			>
				<a
					:href="href"
					class="header-nav__link"
					:class="{ 'header-nav__link--active': isActive(link) }"
					@click="onClick(link, navigate, $event)"
				>
					{{ link.label }}
				</a>
			</router-link>
			<span
				v-else
				class="header-nav__link header-nav__link--stub"
			>
				{{ link.label }}
			</span>
		</template>
	</nav>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useRoute } from "vue-router"
import type { RouteLocationRaw } from "vue-router"
import { workerStore } from "@/store/worker"
import { chatsStore } from "@/store/chats"
import type { Worker } from "@/ api/WorkerApi"

type AccessKey = "CalendarAccess" | "AnalyticsAccess" | "StorageAccess"

type NavLink = {
	label: string
	route?: string
	accessKey?: AccessKey
	isStub: boolean
}

const route = useRoute()
const wStore = workerStore()

const links: NavLink[] = [
	{ label: "Главная",   route: "/main",      isStub: false },
	{ label: "Календарь", route: "/calendar",  accessKey: "CalendarAccess",  isStub: false },
	{ label: "Аналитика", route: "/analytics", accessKey: "AnalyticsAccess", isStub: false },
	{ label: "Настройки", route: "/settings",  isStub: false },
	{ label: "Склад",     route: "/storage",   accessKey: "StorageAccess",   isStub: false }
]

const isAllowed = (link: NavLink, profile: Worker | null) => {
	if (!link.accessKey) {
		return true
	}
	if (!profile) {
		return false
	}
	const value = profile[link.accessKey]
	if (link.accessKey === "StorageAccess") {
		return Number(value) > 0
	}
	return Boolean(value)
}

const visibleLinks = computed(() =>
	links.filter(link => isAllowed(link, wStore.workerProfile))
)

const isHomeLink = (link: NavLink) => link.route === '/main'

const linkTarget = (link: NavLink): RouteLocationRaw => {
	if (isHomeLink(link)) {
		return {
			path: '/main',
			query: {
				page: 'chat',
				mode: 'list'
			}
		}
	}
	return link.route as string
}

const isActive = (link: NavLink) => {
	if (isHomeLink(link)) {
		return route.path === '/main' && route.query.page === 'chat'
	}
	return link.route === route.path
}

const onClick = (
	link: NavLink,
	navigate: (e: MouseEvent) => void,
	e: MouseEvent
) => {
	// Модифицирующий/средний клик — отдаём браузеру, чтобы открылась новая вкладка
	if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
		return
	}

	if (isHomeLink(link)) {
		chatsStore().setMode('list')
		chatsStore().setOpenedChat(null)
	}

	navigate(e)
}
</script>

<style scoped>
.header-nav {
	display: flex;
	align-items: center;
	gap: 24px;
}

.header-nav__link {
	display: inline-block;
	background: transparent;
	border: none;
	color: #fff;
	font-size: 20px;
	font-weight: 500;
	line-height: 1;
	padding: 4px 0;
	border-bottom: 2px solid transparent;
	cursor: pointer;
	text-decoration: none;
	font-family: inherit;
}

.header-nav__link:hover,
.header-nav__link:focus,
.header-nav__link:visited {
	color: #fff;
}

.header-nav__link--stub {
	cursor: default;
}

.header-nav__link--active {
	border-bottom-color: #fff;
}
</style>
